import { useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useTranslation } from 'react-i18next';
import { BsCheck2Circle } from 'react-icons/bs';
import { MdOutlineContentCopy, MdOutlineOpenInNew } from 'react-icons/md';
import { useFlow } from 'contexts/FlowContext';
import { useOneClick } from 'contexts/OneClickBuyContext';
import { bankSlipView } from 'helpers/bankSlipView';
import { isFlowV2Step } from 'utils/flowV2Step';
import Clipboard from '../../Clipboard';
import { ButtonClose } from '../styles/ButtonClose';
import { ButtonSuccess } from '../styles/ButtonSuccess';
import TransactionDetails from '../TransactionDetails';
import {
	BankSlipAction,
	BankSlipPaymentContainer,
	CloseRow,
	Content,
	DetailsWrapper,
	DigitableLine,
	DueDate,
	EmailNotice,
	Header,
	PaymentBlock,
	Title,
} from './BankSlipPaymentContainer';

const BankSlipPayment: React.FC = () => {
	const { actualTransaction, paymentResponse, getHost, setIsModalOpen, redirect } = useOneClick();
	const { getFlowUUID } = useFlow();
	const { t } = useTranslation('upsell');
	const { digitableLine, dueDate, bankSlipUrl } = bankSlipView(actualTransaction, paymentResponse?.hash, getHost());
	const [clipboard, setClipboard] = useState({
		value: '',
		copied: false,
	});
	const hasTransactionDetails = Boolean(paymentResponse?.items?.[0]?.product?.name);
	const canContinueFlow = Boolean(paymentResponse?.hash) && isFlowV2Step(getFlowUUID(), window.location.search);

	return (
		<BankSlipPaymentContainer>
			<Header>
				<Title>
					<BsCheck2Circle />
					<h3>{String(t('payment.bankSlipGenerated'))}</h3>
				</Title>
			</Header>
			<Content>
				<PaymentBlock>
					{dueDate ? (
						<DueDate>
							<span>{String(t('payment.bankSlipDueDate'))}</span>{' '}
							<strong>{dueDate}</strong>
						</DueDate>
					) : null}
					{digitableLine ? (
						<>
							<DigitableLine>
								<Clipboard text={digitableLine} clipboard={clipboard} setClipboard={setClipboard} />
							</DigitableLine>
							<CopyToClipboard
								text={digitableLine}
								onCopy={() => setClipboard({ value: digitableLine, copied: true })}
							>
								<ButtonSuccess>
									{clipboard.copied ? (
										<>
											{String(t('clipboard.copied'))} <BsCheck2Circle />
										</>
									) : (
										<>
											{String(t('clipboard.copyBankSlip'))} <MdOutlineContentCopy />
										</>
									)}
								</ButtonSuccess>
							</CopyToClipboard>
						</>
					) : (
						<EmailNotice>{String(t('payment.bankSlipEmailNotice'))}</EmailNotice>
					)}
				</PaymentBlock>
				{bankSlipUrl ? (
					<BankSlipAction
						href={bankSlipUrl}
						target='_blank'
						rel='noopener noreferrer'
						$emphasis={digitableLine ? 'secondary' : 'primary'}
					>
						{String(t('actions.viewBankSlip'))} <MdOutlineOpenInNew />
					</BankSlipAction>
				) : null}
				{hasTransactionDetails ? (
					<DetailsWrapper>
						<TransactionDetails />
					</DetailsWrapper>
				) : null}
				{canContinueFlow ? (
					<ButtonSuccess onClick={() => redirect(paymentResponse)}>{String(t('actions.continueFlow'))}</ButtonSuccess>
				) : null}
				<CloseRow>
					<ButtonClose onClick={() => setIsModalOpen(false)}>{String(t('actions.close'))}</ButtonClose>
				</CloseRow>
			</Content>
		</BankSlipPaymentContainer>
	);
};

export default BankSlipPayment;
